/* ============================================================
   sh-signature-2.js
   Motion for the second Sherpa Signature page design
   (sections/sh-signature-2.liquid, assets/sh-signature-2.css),
   October 8, 2026. Same rules as assets/sherpa-motion.js: text, prices
   and links are never hidden or moved waiting for motion, everything
   that opens plays once, and only transform and clip-path change.

   1. The hero film: loaded only on screens 750px and wider, for visitors
      who have not asked for reduced motion or to save data, and only once
      the page has loaded, so it never competes with the photo that shows
      first. It fades in over the photo once it plays and pauses while the
      hero is off screen.
   2. Photos open from an inset frame the first time they come into view
      ([data-sg-reveal]); frames already on screen are simply shown.
   3. Photos drift a little against the scroll ([data-sg-drift]) and the
      hero photo sinks slowly as the page moves up, which reads as depth.

   Steps 2 and 3 run only with IntersectionObserver, without reduced
   motion and outside the theme editor; otherwise the page stays still.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-sg]');
  if (!root) return;

  var hero = root.querySelector('[data-sg-hero]');
  var film = root.querySelector('[data-sg-film]');
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- 1. the hero film ---------- */
  if (hero && film) {
    var wide = window.matchMedia('(min-width: 750px) and (prefers-reduced-motion: no-preference)');
    var saveData = navigator.connection && navigator.connection.saveData;
    var loaded = false;
    var heroVisible = true;

    film.addEventListener('playing', function () {
      hero.classList.add('is-playing');
    });

    var updateFilm = function () {
      if (!wide.matches || saveData) {
        if (!film.paused) film.pause();
        return;
      }
      if (!loaded) {
        var sources = film.querySelectorAll('source[data-src]');
        for (var i = 0; i < sources.length; i++) sources[i].src = sources[i].getAttribute('data-src');
        film.preload = 'auto';
        film.load();
        loaded = true;
      }
      if (heroVisible) {
        var playing = film.play();
        if (playing && playing.catch) playing.catch(function () {});
      } else if (!film.paused) {
        film.pause();
      }
    };

    var startFilm = function () {
      updateFilm();
      if (wide.addEventListener) wide.addEventListener('change', updateFilm);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          heroVisible = entries[0].isIntersecting;
          if (loaded) updateFilm();
        }).observe(hero);
      }
    };

    if (document.readyState === 'complete') startFilm();
    else window.addEventListener('load', startFilm, { once: true });
  }

  /* ---------- 2 and 3: gate ---------- */
  if (!('IntersectionObserver' in window)) return;
  if (reduceQuery.matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  root.classList.add('sg-motion');

  /* ---------- 2. photos open ---------- */
  var reveals = root.querySelectorAll('[data-sg-reveal]');
  var vh = window.innerHeight;
  var pending = [];
  for (var r = 0; r < reveals.length; r++) {
    var box = reveals[r].getBoundingClientRect();
    if (box.top < vh && box.bottom > 0) reveals[r].classList.add('sg-in');
    else pending.push(reveals[r]);
  }

  void root.offsetHeight;
  requestAnimationFrame(function () {
    root.classList.add('sg-ready');
    var opener = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sg-in');
        opener.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });
    pending.forEach(function (el) { opener.observe(el); });
  });

  /* ---------- 3. drift ---------- */
  var drifts = Array.prototype.slice.call(root.querySelectorAll('[data-sg-drift]'));
  var media = hero && hero.querySelector('[data-sg-hero-media]');
  var visible = new Set();
  var heroOn = true;
  var ticking = false;

  var watcher = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.target === hero) heroOn = entry.isIntersecting;
      else if (entry.isIntersecting) visible.add(entry.target);
      else visible.delete(entry.target);
    });
    request();
  }, { rootMargin: '10% 0px' });

  drifts.forEach(function (img) { watcher.observe(img.parentNode); });
  if (hero) watcher.observe(hero);

  function paint() {
    ticking = false;
    var h = window.innerHeight;
    visible.forEach(function (frame) {
      var b = frame.getBoundingClientRect();
      /* -1 when the frame's centre is at the bottom of the screen, 1 at
         the top; the photo has 6% spare above and below. */
      var p = ((h / 2) - (b.top + b.height / 2)) / (h / 2 + b.height / 2);
      p = Math.max(-1, Math.min(1, p));
      var img = frame.querySelector('[data-sg-drift]');
      if (img) img.style.setProperty('--drift', (p * b.height * 0.05).toFixed(1) + 'px');
    });
    if (media && heroOn) {
      var y = Math.max(0, window.scrollY || window.pageYOffset);
      media.style.setProperty('--lift', (y * 0.32).toFixed(1) + 'px');
    }
  }

  function request() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(paint);
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });
  request();
})();
